import React, { useState, useEffect, useCallback } from "react";
import { Layout, Menu, Avatar, Space, Button, Tooltip, Result } from "antd";
import styles from "./Layout.module.css";
import {
  MedicineBoxOutlined, UserOutlined, LogoutOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, SwapOutlined,
  BankOutlined, SettingOutlined, HistoryOutlined, TeamOutlined
} from "@ant-design/icons";
import {
  IconLayoutDashboard, IconHeartbeat, IconVirus, IconShieldCheck,
  IconUsers, IconNetwork, IconFileInvoice, IconStethoscope,
  IconStack2, IconTags, IconDna, IconChartBar,
} from "@tabler/icons-react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { ALL_PAGES } from "../pages/UserManagment/CompanyPagesControl/CompanyPagesControl";
import {
  PAGES_CHANGED_EVENT,
  getCurrentRole,
  getSelectedCompanyId,
  loadEnabledPagesForCompany,
  isPageAllowed,
} from "../utils/pagePermissions"; 
import { logout } from "../api/endpoints/auth";

const { Header, Sider, Content } = Layout;

interface User    { id: string; name: string; role: string; organizationName: string | null; }
interface Company { id: string; name: string; username: string; country: string; city: string; active: boolean; transactions: number; endedAt: string; }

const PAGE_ICONS: Record<string, React.ReactNode> = {
  "dashboard":                  <IconLayoutDashboard size={16} stroke={2} style={{ color: "#3b82f6" }} />,
  "tpa-payer":                  <IconUsers          size={16} stroke={2} style={{ color: "#10b981" }} />,
  "network-plan":               <IconNetwork         size={16} stroke={2} style={{ color: "#f59e0b" }} />,
  "policy":                     <IconFileInvoice     size={16} stroke={2} style={{ color: "#ec4899" }} />,
  "specialty":                  <IconStethoscope     size={16} stroke={2} style={{ color: "#8b5cf6" }} />,
  "doctor":                     <IconUsers          size={16} stroke={2} style={{ color: "#06b6d4" }} />,
  "customicd":                  <IconTags            size={16} stroke={2} style={{ color: "#a855f7" }} />,
  "customactivity":             <IconStack2          size={16} stroke={2} style={{ color: "#6366f1" }} />,
  "medicalicd":                 <IconVirus           size={16} stroke={2} style={{ color: "#ef4444" }} />,
  "medicalactivity":            <IconHeartbeat       size={16} stroke={2} style={{ color: "#f43f5e" }} />,
  "icd-not-covered":            <IconVirus           size={16} stroke={2} style={{ color: "#ea580c" }} />,
  "icd-not-allowed-principal":  <IconVirus           size={16} stroke={2} style={{ color: "#dc2626" }} />,
  "icd-age-gender":             <IconVirus           size={16} stroke={2} style={{ color: "#14b8a6" }} />,
  "activity-not-covered":       <IconHeartbeat       size={16} stroke={2} style={{ color: "#d946ef" }} />,
  "activity-age-gender":        <IconHeartbeat       size={16} stroke={2} style={{ color: "#65a30d" }} />,
  "activities-not-allowed":     <IconHeartbeat       size={16} stroke={2} style={{ color: "#b91c1c" }} />,
  "activities-allowed":         <IconHeartbeat       size={16} stroke={2} style={{ color: "#16a34a" }} />,
  "activity-not-allowed-icd":   <IconHeartbeat       size={16} stroke={2} style={{ color: "#c026d3" }} />,
  "activity-pre-approvals":     <IconShieldCheck     size={16} stroke={2} style={{ color: "#2563eb" }} />,
  "activity-supporting-docs":   <IconFileInvoice     size={16} stroke={2} style={{ color: "#4f46e5" }} />,
  "activity-frequency":         <IconChartBar        size={16} stroke={2} style={{ color: "#059669" }} />,
  "activity-bundle":            <IconChartBar        size={16} stroke={2} style={{ color: "#059669" }} />,
  "claim-validation":           <IconShieldCheck     size={16} stroke={2} style={{ color: "#0284c7" }} />,
  "bulk-claim-validation":      <IconShieldCheck     size={16} stroke={2} style={{ color: "#4338ca" }} />,
  "claim-validation-history":   <IconDna             size={16} stroke={2} style={{ color: "#db2777" }} />,
};

const AdminLayout = () => {
  const [user, setUser]                 = useState<User | null>(null);
  const [selectedCompany, setCompany] = useState<Company | null>(null);
  const [collapsed, setCollapsed]     = useState(false);
  const [menuItems, setMenuItems]     = useState<any[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  const companyId = getSelectedCompanyId();
  const isAuditLogVisible = ["Super Super Admin", "System Admin"].includes(getCurrentRole()) && !!companyId;

  const getCurrentPageKey = () => {
    for (const group of ALL_PAGES) {
      const found = group.pages.find((p) => p.route === location.pathname);
      if (found) return found.key;
    }
    return null;
  };

  const currentPageKey = getCurrentPageKey();
  const isCurrentPageAllowed = currentPageKey ? isPageAllowed(currentPageKey, companyId) : true;

  const buildMenu = useCallback((enabled: string[]) => {
    const items: any[] = [];
    ALL_PAGES.forEach((group) => {
      const groupPages = group.pages.map((p) => ({
        ...p,
        isEnabled: enabled.includes(p.key),
      }));

      if (groupPages.length === 0) return;

      const groupKey = group.group.toLowerCase().replace(/\s+/g, "-");

      if (group.group === "Core" && groupPages.length === 1) {
        const p = groupPages[0];
        const isSelected = location.pathname === p.route;
        items.push({
          key:   p.route,
          icon:  PAGE_ICONS[p.key],
          label: (
            <div data-testid={`sidebar-nav-${p.key}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: 4, opacity: p.isEnabled ? 1 : 0.75 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{p.label}</span>
              {!p.isEnabled && (
                <span data-testid={`sidebar-nav-off-${p.key}`} style={{ 
                  fontSize: "10px", 
                  backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.08)", 
                  color: isSelected ? "#ffffff" : "#9ca3af", 
                  padding: "1px 6px", 
                  borderRadius: "4px", 
                  fontWeight: isSelected ? 600 : 500,
                  lineHeight: "14px",
                  border: isSelected ? "1px solid rgba(255, 255, 255, 0.4)" : "1px solid rgba(255, 255, 255, 0.12)",
                  flexShrink: 0,
                  marginLeft: 8
                }}>
                  OFF
                </span>
              )}
            </div>
          ),
        });
      } else {
        const GroupIcon = PAGE_ICONS[groupPages[0]?.key] || <SettingOutlined />;
        items.push({
          key:    `group_${group.group}`,
          icon:   GroupIcon,
          label:  (
            <span data-testid={`sidebar-group-${groupKey}`}>
              {group.group}
            </span>
          ),
          children: groupPages.map((p) => {
            const isSelected = location.pathname === p.route;
            return {
              key:   p.route,
              icon:  PAGE_ICONS[p.key],
              label: (
                <div data-testid={`sidebar-nav-${p.key}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: 4, opacity: p.isEnabled ? 1 : 0.75 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{p.label}</span>
                  {!p.isEnabled && (
                    <span data-testid={`sidebar-nav-off-${p.key}`} style={{ 
                      fontSize: "10px", 
                      backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.08)", 
                      color: isSelected ? "#ffffff" : "#9ca3af", 
                      padding: "1px 6px", 
                      borderRadius: "4px", 
                      fontWeight: isSelected ? 600 : 500,
                      lineHeight: "14px",
                      border: isSelected ? "1px solid rgba(255, 255, 255, 0.4)" : "1px solid rgba(255, 255, 255, 0.12)",
                      flexShrink: 0,
                      marginLeft: 8
                    }}>
                      OFF
                    </span>
                  )}
                </div>
              ),
            };
          }),
        });
      }
    });
    return items;
  }, [location.pathname]);

  const syncFromStorage = useCallback(async () => {
    const coStr = sessionStorage.getItem("selectedCompany");
    if (!coStr) { 
      setCompany(null); 
      setMenuItems([]); 
      setPermissionsLoaded(true);
      return; 
    }
    try {
      const co: Company = JSON.parse(coStr);
      setCompany(co);

      const enabled = await loadEnabledPagesForCompany(co.id);
      setMenuItems(buildMenu(enabled));
    } catch (e) {
      console.error("AdminLayout syncFromStorage error:", e);
    } finally {
      setPermissionsLoaded(true);
    }
  }, [buildMenu]);

  useEffect(() => {
    const userStr = sessionStorage.getItem("user");
    if (userStr) { try { setUser(JSON.parse(userStr)); } catch {} }
    syncFromStorage();
  }, [syncFromStorage]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { companyId: string; pages: string[] } | undefined;
      if (!detail) { syncFromStorage(); return; }
      const currentId = getSelectedCompanyId();
      if (detail.companyId !== currentId) return; 
      setMenuItems(buildMenu(detail.pages));
    };
    window.addEventListener(PAGES_CHANGED_EVENT, handler);
    window.addEventListener("companyChanged", syncFromStorage);
    return () => {
      window.removeEventListener(PAGES_CHANGED_EVENT, handler);
      window.removeEventListener("companyChanged", syncFromStorage);
    };
  }, [syncFromStorage, buildMenu]);

  useEffect(() => { syncFromStorage(); }, [location.pathname, syncFromStorage]);

  const handleSwitchCompany = () => {
    sessionStorage.removeItem("selectedCompany");
    navigate("/admin/companies");
  };

  const handleLogout = async () => {
    try { await logout(); } catch {}
    finally { sessionStorage.clear(); navigate("/login"); }
  };

  return (
    <Layout className={styles.layout} data-testid="AdminLayout-root">
      <Sider trigger={null} collapsible collapsed={collapsed} className={styles.sider} width={collapsed ? 80 : 260} data-testid="AdminLayout-sider">

        <div className={styles.logo} data-testid="AdminLayout-logo-container">
          {!collapsed ? (
            <div className={styles.logoText} data-testid="AdminLayout-logo-expanded"><MedicineBoxOutlined className={styles.logoIcon} />QUDRA</div>
          ) : (
            <MedicineBoxOutlined className={styles.collapsedLogoIcon} data-testid="AdminLayout-logo-collapsed" />
          )}
        </div>

        {selectedCompany && !collapsed && (
          <div style={{ margin:"0 12px 10px", background:"rgba(59,130,246,0.1)", border:"1px solid rgba(59,130,246,0.22)", borderRadius:12, padding:"10px 12px" }} data-testid="AdminLayout-company-badge-expanded">
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <div style={{ width:32, height:32, borderRadius:9, background:"rgba(59,130,246,0.18)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <BankOutlined style={{ color:"#60a5fa", fontSize:14 }} />
              </div>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} data-testid="AdminLayout-company-name">{selectedCompany.name}</div>
              </div>
            </div>
            <Button size="small" icon={<SwapOutlined />} onClick={handleSwitchCompany}
              style={{ width:"100%", fontSize:11, height:26, borderColor:"rgba(59,130,246,0.35)", color:"#93c5fd", background:"transparent" }}
              data-testid="AdminLayout-switch-company-btn">
              Switch Company
            </Button>
          </div>
        )}

        {selectedCompany && collapsed && (
          <Tooltip title={selectedCompany.name} placement="right">
            <div onClick={handleSwitchCompany} style={{ 
              margin: "0 16px 15px", 
              height: 40, 
              background: "rgba(59,130,246,0.1)", 
              border: "1px solid rgba(59,130,246,0.22)", 
              borderRadius: 8, 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center", 
              cursor: "pointer" 
            }} data-testid="AdminLayout-company-badge-collapsed">
              <SwapOutlined style={{ color: "#60a5fa", fontSize: 16 }} />
            </div>
          </Tooltip>
        )}

        <Menu 
          theme="dark" 
          mode="inline" 
          className={styles.menu} 
          selectedKeys={[location.pathname]} 
          items={menuItems} 
          onClick={({ key }) => navigate(key)} 
          data-testid="AdminLayout-sidebar-menu"
        />

        {!collapsed && selectedCompany && (
          <div style={{ padding:"10px 12px 20px", marginTop:"auto", display:"flex", flexDirection:"column", gap:4 }} data-testid="AdminLayout-actions-expanded">
            <Button type="text" icon={<SettingOutlined />} onClick={() => navigate("/admin/pages-control")}
              style={{ width:"100%", textAlign:"left", color:"rgba(255,255,255,0.4)", fontSize:12, display:"flex", alignItems:"center", gap:6 }}
              data-testid="AdminLayout-pages-control-btn">
              Page visibility settings
            </Button>
            <Button type="text" icon={<TeamOutlined />} onClick={() => navigate("/admin/employees")}
              style={{ width:"100%", textAlign:"left", color:"rgba(255,255,255,0.4)", fontSize:12, display:"flex", alignItems:"center", gap:6 }}
              data-testid="AdminLayout-employees-btn">
              Account Management 
            </Button>
            {isAuditLogVisible && (
              <Button type="text" icon={<HistoryOutlined />} onClick={() => navigate("/audit-log")}
                style={{ width:"100%", textAlign:"left", color:"rgba(255,255,255,0.4)", fontSize:12, display:"flex", alignItems:"center", gap:6 }}
                data-testid="AdminLayout-audit-log-btn">
                Audit Log
              </Button>
            )}
          </div>
        )}

        {collapsed && selectedCompany && (
          <div style={{ 
            padding: "15px 0 25px", 
            marginTop: "auto", 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            gap: 12 
          }} data-testid="AdminLayout-actions-collapsed">
            <Tooltip title="Page visibility settings" placement="right">
              <Button type="text" icon={<SettingOutlined />} onClick={() => navigate("/admin/pages-control")}
                style={{ color:"rgba(255,255,255,0.4)", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center", width: 40, height: 40 }} 
                data-testid="AdminLayout-pages-control-collapsed-btn" />
            </Tooltip>
            <Tooltip title="Employee Management" placement="right">
              <Button type="text" icon={<TeamOutlined />} onClick={() => navigate("/admin/employees")}
                style={{ color:"rgba(255,255,255,0.4)", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center", width: 40, height: 40 }} 
                data-testid="AdminLayout-employees-collapsed-btn" />
            </Tooltip>
            {isAuditLogVisible && (
              <Tooltip title="Audit Log" placement="right">
                <Button type="text" icon={<HistoryOutlined />} onClick={() => navigate("/audit-log")}
                  style={{ color:"rgba(255,255,255,0.4)", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center", width: 40, height: 40 }} 
                  data-testid="AdminLayout-audit-log-collapsed-btn" />
              </Tooltip>
            )}
          </div>
        )}

      </Sider>

      <Layout className={styles.mainLayout} style={{ marginLeft: collapsed ? 80 : 260 }} data-testid="AdminLayout-main-container">
        <Header className={styles.header} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }} data-testid="AdminLayout-header">
          
          <Button 
            type="text" 
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} 
            onClick={() => setCollapsed(!collapsed)} 
            className={styles.headerButton} 
            data-testid="AdminLayout-collapse-toggle-btn"
          />

          <Space size="large" align="center" style={{ marginLeft: "auto", flexShrink: 0 }}>
            <div className={styles.userSection} data-testid="AdminLayout-user-section">
              <div className={styles.userInfo} data-testid="AdminLayout-user-info">
                <Avatar size={40} className={styles.avatar} icon={<UserOutlined style={{ color: "#1890ff" }} />} data-testid="AdminLayout-user-avatar" />
                <div className={styles.userText}>
                  <div className={styles.userName} data-testid="AdminLayout-user-name">{user?.name || "User"}</div>
                  <div className={styles.userRole} data-testid="AdminLayout-user-role">{user?.organizationName || user?.role || "Admin"}</div>
                </div>
              </div>
              <div className={styles.divider} />
              <Button type="text" danger icon={<LogoutOutlined />} onClick={handleLogout} className={styles.logoutButton} data-testid="AdminLayout-logout-btn">
                Logout
              </Button>
            </div>
          </Space>

        </Header>
        <Content className={styles.content} data-testid="AdminLayout-content">
          {!permissionsLoaded ? null : !isCurrentPageAllowed ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}>
              <Result
                status="403"
                title="Access Denied"
                subTitle="This feature has been disabled for your company by the system administrator."
              />
            </div>
          ) : (
            <Outlet />
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;